import { useRef, useEffect, useState } from "react";
import classNames from "classnames/bind";
import { ChevronUpOutlined as ChevronUp } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronUpOutlined";
import { ChevronDownOutlined as ChevronDown } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronDownOutlined";
import styles from "./Thumbnails.module.scss";
import { useInView } from "react-intersection-observer";
import { dispatchGalleryMonetateQEvent } from "../Analytics/dispatchGalleryMonetateQEvent";
import { GalleryObject } from "@/components/Galleries/utils";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { imageProfileGenerator } from "@/utils/image/imageProfileGenerator";

const cx = classNames.bind(styles);

type ThumbnailProps = {
  media: GalleryObject[];
  scrollToIndex: (index: number) => void;
  selectedInd: number;
  className?: string;
  fullscreen?: boolean;
  transparent?: boolean;
  imageSizes?: string;
  useResponsiveImages?: boolean;
  carouselGalleryInpTest?: boolean;
  ref?: React.RefObject<HTMLDivElement | null>;
};
export const Thumbnails = ({
  ref,
  media,
  scrollToIndex,
  selectedInd,
  className,
  fullscreen,
  transparent,
  imageSizes = "",
  useResponsiveImages = false,
  carouselGalleryInpTest = false,
}: ThumbnailProps) => {
  const [prevButtonDisabled, setPrevButtonDisabled] = useState(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const modalMode = media[0]?.type === "video" ? "videoModal" : "imageModal";

  const { ref: firstItemRef, inView: firstItemInView } = useInView({
    root: wrapperRef.current,
    threshold: 1,
    initialInView: true,
  });
  const { ref: lastItemRef, inView: lastItemInView } = useInView({
    root: wrapperRef.current,
    threshold: 1,
    initialInView: true,
  });

  useEffect(() => {
    setPrevButtonDisabled(firstItemInView);
    setNextButtonDisabled(lastItemInView);
  }, [firstItemInView, lastItemInView]);

  const handleButtonNextClick = () => {
    if (typeof ref === "function" || !ref || !ref.current) {
      return;
    }

    const distance = ref.current.clientHeight / 2;
    const scrollTop = ref.current.scrollTop;

    ref.current.scrollTo({
      top: scrollTop + distance,
      behavior: "smooth", // Added smooth scrolling
    });

    dispatchGalleryMonetateQEvent(
      "ThumbnailsDownArrow",
      fullscreen ? modalMode : "generic",
      undefined,
      carouselGalleryInpTest,
    );
  };
  const handleButtonPrevClick = () => {
    if (typeof ref === "function" || !ref || !ref.current) {
      return;
    }

    const distance = ref.current.clientHeight / 2;
    const scrollTop = ref.current.scrollTop;

    ref.current.scrollTo({
      top: scrollTop - distance,
      behavior: "smooth",
    });

    dispatchGalleryMonetateQEvent(
      "ThumbnailsUpArrow",
      fullscreen ? modalMode : "generic",
      undefined,
      carouselGalleryInpTest,
    );
  };

  const getRef = (ind: number) => {
    if (ind === 0) {
      return firstItemRef;
    }
    if (ind === media.length - 1) {
      return lastItemRef;
    }
    return null;
  };

  return (
    <div className={cx(styles.wrapper, className)} ref={wrapperRef}>
      <div
        className={cx(styles.thumbnails, {
          fullscreen,
        })}
        ref={ref}
      >
        {media.map((mediaItem, ind) => (
          <div
            key={`${mediaItem.imageUrl}-${ind}`}
            className={cx(styles.thumbnailItem, {
              "thumbnailItem--selected": ind === selectedInd,
            })}
          >
            <button
              className={cx(styles.thumbnailItemButton, {
                video: mediaItem.type === "video",
                "thumbnailItemButton--transparent": transparent,
              })}
              onClick={() => {
                scrollToIndex(ind);
                dispatchGalleryMonetateQEvent(
                  "ThumbnailItem",
                  fullscreen ? modalMode : "generic",
                  undefined,
                  carouselGalleryInpTest,
                );
              }}
              ref={getRef(ind)}
              data-testid={`standard:gallery:thumbnails:thumbnail-${ind}`}
              aria-label={`Product Image ${ind + 1} of ${media.length}`}
            >
              <img
                src={formatAmplienceImageURL(mediaItem.imageUrl)}
                alt=""
                className={styles.thumbnailImage}
                srcSet={
                  useResponsiveImages
                    ? imageProfileGenerator(mediaItem.imageUrl)
                    : undefined
                }
                sizes={useResponsiveImages ? imageSizes : undefined}
              />
            </button>
          </div>
        ))}
      </div>
      <button
        data-testid="standard:gallery:scroll:button:previous"
        className={cx(styles.buttonPrev, {
          disabled: prevButtonDisabled,
        })}
        onClick={handleButtonPrevClick}
        aria-label="Scroll Image Gallery Up"
      >
        <ChevronUp size="medium" />
      </button>
      <button
        data-testid="standard:gallery:scroll:button:next"
        className={cx(styles.buttonNext, {
          disabled: nextButtonDisabled,
        })}
        onClick={handleButtonNextClick}
        aria-label="Scroll Image Gallery Down"
      >
        <ChevronDown size="medium" />
      </button>
    </div>
  );
};

Thumbnails.displayName = "Thumbnails";
